import { AxiosError } from 'axios'
import { Button, DataTableController } from 'components/common'
import {
  columns,
  FILTER_FIELD_ORDERS,
  OrderFilterForm,
  OrdersTable,
} from 'components/orderManagement'
import { usePageAccess, useQuery } from 'hooks'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import toast from 'react-hot-toast'
import {
  downloadOrders,
  triggerOrderSync,
  triggerReconciliationSync,
} from 'utils/api/orderApi'
import { filterEmptyValues } from 'utils/helpers'

const CRON_TYPE = {
  ORDER: 'Order Sync',
  RECONCILIATION: 'Reconciliation Sync',
}

const OrderListPage = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [triggerLoading, setTriggerLoading] = useState<string>()

  const { getQuery } = useQuery()

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_ORDERS)

    // if not filters found in URL
    if (!filters) {
      return toast.error('Filters are required to export data!', {
        duration: 3000,
      })
    }

    // filtering empty values and keys of filter
    const masterFilter = filterEmptyValues(filters)

    // if filters are present with empty values
    if (Object.keys(masterFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await downloadOrders({ filters: masterFilter })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error('Failed to export Orders Data.', {
        id: loadingToast,
        duration: 3000,
      })
    }
  }

  const triggerCron = async (jobTitle: string) => {
    const loadingToast = toast.loading(`Initializing ${jobTitle} Job`, {
      duration: Infinity,
    })

    setTriggerLoading(jobTitle)

    try {
      if (jobTitle === CRON_TYPE.ORDER) {
        await triggerOrderSync()
      } else {
        await triggerReconciliationSync()
      }

      toast.success(`${jobTitle} Job Initiated`, {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error?.response?.data?.errors?.msg?.runningService) {
          return toast.error(error.response.data.errors.msg.runningService, {
            id: loadingToast,
            duration: 3000,
          })
        }
      }
      toast.error(`Error initiating ${jobTitle} Job`, {
        id: loadingToast,
        duration: 3000,
      })
    } finally {
      setTriggerLoading(undefined)
    }
  }

  return (
    <>
      <Helmet>
        <title>ORMS Order Management</title>
      </Helmet>

      {isEditor && (
        <>
          <h3 className="title">Manual Sync</h3>

          <div className="mb-5 flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              buttonText="Resync Orders"
              loading={triggerLoading === CRON_TYPE.ORDER}
              onClick={() => triggerCron(CRON_TYPE.ORDER)}
            />
            <Button
              type="button"
              variant="outline"
              buttonText="Resync Reconciliation"
              loading={triggerLoading === CRON_TYPE.RECONCILIATION}
              onClick={() => triggerCron(CRON_TYPE.RECONCILIATION)}
            />
          </div>
        </>
      )}

      <div
        className={`${
          isEditor ? 'mt-4' : ''
        } mb-2 flex flex-wrap items-center justify-between  md:mb-0 md:flex-nowrap`}
      >
        <h3 className="title">Current Orders</h3>
      </div>

      {/* Orders Table */}
      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleExport={handleExport}
        activeColumnKey="order"
        placeholder="Search by Order ID, Invoice No and Short Code"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
      />

      {showFilter ? (
        <OrderFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <OrdersTable
          setSelectedColumns={setSelectedColumns}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default OrderListPage
